import { useEffect, useRef, useState } from "react";

/**
 * useVersionCheck — detecta cuando se publicó un build nuevo del frontend
 * SIN que el usuario tenga que refrescar manualmente la pestaña.
 *
 * Usa `asset-manifest.json`, un archivo que `react-scripts build` genera
 * automáticamente en cada build con los nombres de archivo hasheados
 * (ej. "main.a1b2c3d4.js") — cambia solo cuando el código realmente cambió,
 * así que no requiere mantener ningún número de versión a mano.
 *
 * Se compara contra el manifest cargado al montar (baseline). Si en un poll
 * posterior el hash es distinto, significa que se desplegó un build nuevo
 * mientras el usuario tenía la pestaña abierta.
 */
const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 min

async function fetchBuildId() {
  const res = await fetch(`/asset-manifest.json?_=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) throw new Error("sin asset-manifest.json (¿modo desarrollo?)");
  const json = await res.json();
  return json.files?.["main.js"] || JSON.stringify(json.entrypoints || {});
}

export function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const baselineRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        const id = await fetchBuildId();
        if (cancelled) return;
        if (baselineRef.current === null) {
          baselineRef.current = id;
        } else if (id !== baselineRef.current) {
          setUpdateAvailable(true);
        }
      } catch {
        // En "npm start" (dev) no existe asset-manifest.json — se ignora.
      }
    }

    check();
    const timer = setInterval(check, CHECK_INTERVAL_MS);
    // Revisar también al volver a la pestaña — captura updates mientras
    // el usuario estuvo en otra ventana con esta pestaña de fondo.
    function onVisible() {
      if (document.visibilityState === "visible") check();
    }
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  return updateAvailable;
}
